/**
 *
 *  Contact Us page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 21/04/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useRef, useState } from "react";
import Modal from "react-modal";
import { Title, Meta } from "react-meta-elements";
import { useAppState } from "../../../state";

// Components
import Header from "../../Header/Header";
import Footer from "../../Footer/Footer";
import Button from "../../Button/Button";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";

// Hooks
import useTheme from "../../ThemeProvider/useTheme/useTheme";

// Icons
import CloseIcon from "../../../icons/CloseIcon";
import CheckNiceIcon from "../../../icons/CheckNiceIcon";

// Utils
import { clientWidth } from "../../../utils";
import { modalStyles } from "../../../constants";
import PhoneIcon from "../../../icons/PhoneIcon";
import WhatsAppIcon from "../../../icons/WhatsAppIcon";

export default function ContactUsPage() {
  const { theme } = useTheme();

  const { user, sendSupportRequest, isFetching } = useAppState();

  const [customerName, setCustomerName] = useState(
    user ? `${user.firstName} ${user.lastName}` : ""
  );
  const [customerEmail, setCustomerEmail] = useState(user ? user.email : "");
  const [customerRequestMessage, setCustomerRequestMessage] = useState("");
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [hasEmailError, setHasEmailError] = useState(false);

  const heroRef = React.useRef(null);
  const customerEmailRef = useRef();
  const customerRequestMessageRef = useRef();
  const infoModalResolveRef = useRef(null);

  const openInfoModal = useCallback((msg, title = "Sorry") => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  const handleCustomerNameChange = useCallback((event) => {
    const value = event.target.value;

    setCustomerName(value);
  }, []);

  const handleCustomerEmailChange = useCallback((event) => {
    const value = event.target.value;

    setCustomerEmail(value);
    setHasEmailError(false);
  }, []);

  const handleCustomerRequestMessageChange = useCallback((event) => {
    const value = event.target.value;

    setCustomerRequestMessage(value);
  }, []);

  const handleCustomerRequestSubmit = useCallback(
    (e) => {
      // const userId = user.id;
      let customerEmail = customerEmailRef.current.value;
      let customerMessage = customerRequestMessageRef.current.value;

      if (!customerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
        setHasEmailError(true);

        return;
      }

      if (customerMessage && customerMessage.length > 1) {
        sendSupportRequest(
          `${customerName}`,
          customerEmail,
          undefined, // subject
          customerMessage
        ).then(() => {
          setCustomerRequestMessage("");
          openInfoModal(
            "Thank you for the feedback, we will contact you soon.",
            "Your request has been sent"
          );
        });
      }
    },
    [customerName] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <>
      <ScrollToTopOnMount />
      {/*<Title>Personal Training in London | TruBe</Title>*/}
      <Meta
        name="description"
        content={`On-demand, in person, personal training in London.`}
      />
      <Header
        behaviour="none"
        isFixed
        isLoggedIn={!!(user && user.id)}
        heroRef={heroRef}
      />

      <div ref={heroRef} className="tbk-relative tbk-bg-grey-light lg:tbk-mb-0">
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 tbk-pt-4 lg:tbk-px-8 lg:tbk-pt-8 lg:tbk-pb-8">
          <div className="md:tbk-grid md:tbk-grid-cols-2 md:tbk-gap-8">
            <div>
              <h1 className="tbk-text-h2-black md:tbk-text-epic-bold tbk-mb-2 !tbk-leading-tight tbk-text-grey-main md:!tbk-leading-tight lg:tbk-mb-3">
                We'd Love to <br className="tbk-hidden lg:tbk-block" />
                Hear From You.
              </h1>
              <p className="tbk-text-main lg:tbk-text-title-medium tbk-mb-3 tbk-text-primary">
                Whether you have questions, comments, or want to chat wellness,
                we’d love to hear it all.
              </p>
              {/*<p className="tbk-text-main lg:tbk-text-title-medium tbk-mb-3 tbk-text-primary">
                <PhoneIcon className="tbk-mr-1 tbk-text-blue-grey" />
                <a href="tel:07459783573" className="tbk-text-primary">
                  +44 (0) 745 978 3573
                </a>
              </p>*/}
              {/*<p className="tbk-text-main lg:tbk-text-title-medium tbk-mb-4 tbk-text-primary">
                <WhatsAppIcon className="tbk-mr-1 tbk-inline-block tbk-text-blue-grey" />
                <a
                  href="https://wa.me/4407459783573"
                  className="tbk-text-primary"
                >
                  TruBe in WhatsApp
                </a>
              </p>*/}
            </div>
            <div className="tbk-relative tbk-z-1">
              <div className="tbk-w-full tbk-rounded-2xl tbk-bg-basic-white tbk-p-3 tbk-shadow-trube">
                <div className="tbk-text-small tbk-mb-0.5 tbk-text-grey-main">
                  Your name:
                </div>
                <input
                  type="text"
                  className={`tbk-input-field tbk-mb-3 tbk-w-full tbk-text-grey-main${
                    hasEmailError ? " tbk-border-coral" : ""
                  }`}
                  name="customerName"
                  placeholder="Your Name"
                  value={customerName}
                  onChange={handleCustomerNameChange}
                />
                <div className="tbk-text-small tbk-mb-0.5 tbk-text-grey-main">
                  Email address:
                </div>
                <input
                  ref={customerEmailRef}
                  type="email"
                  className={`tbk-input-field tbk-mb-3 tbk-w-full tbk-text-grey-main${
                    hasEmailError ? " tbk-border-coral" : ""
                  } tbk-text-ellipsis`}
                  name="customerEmail"
                  placeholder="Your Email"
                  value={customerEmail}
                  onChange={handleCustomerEmailChange}
                />
                <div className="tbk-text-small tbk-mb-0.5 tbk-text-grey-main">
                  Your request:
                </div>
                <textarea
                  ref={customerRequestMessageRef}
                  className="tbk-input-field tbk-mb-3 tbk-w-full tbk-pt-1.5 tbk-pr-2 tbk-pl-2 tbk-pb-3 tbk-text-grey-main"
                  name="customerMessage"
                  rows="5"
                  placeholder="Ask a question or leave a comment"
                  value={customerRequestMessage}
                  onChange={handleCustomerRequestMessageChange}
                />
                <Button
                  color="coral"
                  className="tbk-mb-2 tbk-w-full"
                  disabled={
                    !customerName ||
                    !customerEmail ||
                    !customerRequestMessage ||
                    isFetching
                  }
                  showLoader={isFetching}
                  onClick={handleCustomerRequestSubmit}
                >
                  Submit Form
                </Button>
                <p className="tbk-text-small tbk-text-center tbk-text-grey">
                  By clicking "Submit", you accept our{" "}
                  <a href="/terms/Terms of Use and Booking.pdf" target="_blank">
                    Terms of Use
                  </a>{" "}
                  and{" "}
                  <a
                    href="/terms/TruBe - US Privacy Policy.pdf"
                    target="_blank"
                  >
                    Privacy Policy
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-relative tbk-hidden tbk-h-screen tbk-bg-contact-us-mobile tbk-bg-cover tbk-pt-7 tbk-pb-0 lg:tbk-bg-contact-us lg:tbk-pt-8 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          <h2 className="tbk-text-h2-black lg:tbk-text-epic-bold tbk-hidden tbk-text-center tbk-text-basic-white lg:tbk-mt-4 lg:tbk-text-left">
            Let’s Connect
          </h2>
        </div>
      </div>

      <Footer />
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : modalStyles.info
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          <button
            onClick={closeInfoModal}
            className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-hidden tbk-text-coral"
          >
            <CloseIcon />
          </button>
          <h3 className="tbk-text-title-bold tbk-mt-0 tbk-mb-1 tbk-text-center tbk-text-grey-main">
            Your request has been sent
          </h3>
          <p className="tbk-text-main tbk-mb-4 tbk-max-w-full tbk-text-center tbk-text-grey-main">
            Thank you for the feedback, we will contact you soon.
          </p>
          <CheckNiceIcon size={64} className="tbk-mb-4 tbk-text-green" />
          <Button className="tbk-w-full" onClick={closeInfoModal}>
            Ok
          </Button>
        </div>
      </Modal>
    </>
  );
}
